import { Loading } from "@/components";
import Modal from "@/components/common/Modal";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getIsLoading, setIsApiLoading, setIsLoading } from "@/store/appSlice";
import API from "@/utils/api";
import { ADMIN_STATUS } from "@/utils/constants";
import { yupResolver } from "@hookform/resolvers/yup";
import DT from "datatables.net-dt";
import DataTable from "datatables.net-react";
import { format } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import * as yup from "yup";

DataTable.use(DT);

const createAdminSchema = yup.object().shape({
  email: yup
    .string()
    .matches(
      /^(?=.{1,64}$)[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/,
      "メールアドレスが無効です。"
    )
    .required("メールアドレスが必須です。"),
  password: yup
    .string()
    .required("パスワードが必須です。")
    .min(8, "パスワードは8文字以上にしてください。"),
  status: yup.string().required("ステータスが必須です。"),
  name: yup.string().required("管理者名が必須です。"),
  isAdmin: yup.string().required("権限が必須です。"),
  isContentManageAdmin: yup.boolean().when("isAdmin", (isAdmin, schema) => {
    if (isAdmin && isAdmin[0] === "0") {
      return schema.test(
        "content-or-user-required",
        "権限が必須です。",
        function (value) {
          const { isUserManageAdmin } = this.parent;
          return value || isUserManageAdmin;
        }
      );
    }
    return schema.nullable();
  }),
  isUserManageAdmin: yup.boolean().when("isAdmin", (isAdmin, schema) => {
    if (isAdmin && isAdmin[0] === "0") {
      return schema.test(
        "content-or-user-required",
        "権限が必須です。",
        function (value) {
          const { isContentManageAdmin } = this.parent;
          return value || isContentManageAdmin;
        }
      );
    }
    return schema.nullable();
  }),
});

const editAdminSchema = yup.object().shape({
  email: yup
    .string()
    .matches(
      /^(?=.{1,64}$)[a-zA-Z0-9_+-]+(\.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/,
      "メールアドレスが無効です。"
    )
    .required("メールアドレスが必須です。"),
  password: yup
    .string()
    .required("パスワードが必須です。")
    .min(8, "パスワードは8文字以上にしてください。"),
  status: yup.string().required("ステータスが必須です。"),
  name: yup.string().required("管理者名が必須です。"),
  isAdmin: yup.string().required("権限が必須です。"),
  isContentManageAdmin: yup.boolean().when("isAdmin", (isAdmin, schema) => {
    if (isAdmin && isAdmin[0] === "0") {
      return schema.test(
        "content-or-user-required",
        "権限が必須です。",
        function (value) {
          const { isUserManageAdmin } = this.parent;
          return value || isUserManageAdmin;
        }
      );
    }
    return schema.nullable();
  }),
  isUserManageAdmin: yup.boolean().when("isAdmin", (isAdmin, schema) => {
    if (isAdmin && isAdmin[0] === "0") {
      return schema.test(
        "content-or-user-required",
        "権限が必須です。",
        function (value) {
          const { isContentManageAdmin } = this.parent;
          return value || isContentManageAdmin;
        }
      );
    }
    return schema.nullable();
  }),
});

const Admins = () => {
  const modalRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getIsLoading);
  const [admins, setAdmins] = useState([]);
  const [adminSearch, setAdminSearch] = useState<string>("");
  const [searchStatus, setSearchStatus] = useState<string>("");
  const [activeAdmin, setActiveAdmin] = useState<any>();
  const [showSubRolesCreate, setShowSubRolesCreate] = useState<boolean>(false);
  const [showSubRolesEdit, setShowSubRolesEdit] = useState<boolean>(false);

  const createForm = useForm({
    resolver: yupResolver(createAdminSchema),
  });

  const editForm = useForm({
    resolver: yupResolver(editAdminSchema),
    values: {
      email: activeAdmin?.email,
      name: activeAdmin?.name,
      password: activeAdmin?.password,
      status: activeAdmin?.status,
    },
    defaultValues: {
      email: activeAdmin?.email || "",
      name: activeAdmin?.name || "",
      password: activeAdmin?.password || "",
      status: activeAdmin?.status || "",
      isAdmin: activeAdmin?.isAdmin ? "1" : "0",
      isContentManageAdmin: activeAdmin?.isContentManageAdmin ?? false,
      isUserManageAdmin: activeAdmin?.isUserManageAdmin ?? false,
    },
  });

  useEffect(() => {
    const modalElement = modalRef.current;

    if (modalElement) {
      modalElement.addEventListener("hidden.bs.modal", handleModalHidden);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener("hidden.bs.modal", handleModalHidden);
      }
    };
  }, []);

  const handleModalHidden = () => {
    setActiveAdmin(null);
  };

  useEffect(() => {
    const isAdmin = editForm.getValues().isAdmin;
    setShowSubRolesEdit(isAdmin == "0");
  }, [editForm.watch("isAdmin")]);

  useEffect(() => {
    const isAdmin = createForm.getValues().isAdmin;
    setShowSubRolesCreate(isAdmin == "0");
  }, [createForm.watch("isAdmin")]);

  const fetchAdmins = async () => {
    dispatch(setIsLoading(true));
    try {
      const params = new URLSearchParams();
      params.append("admin", adminSearch.trim());
      params.append("status", searchStatus);
      const response = await API.get(`admins?${params.toString()}`);
      const { data } = response.data;
      const convertData = data.map((item: any) => ({
        ...item,
        lastLoginTime: item.lastLoginTime
          ? format(
            new Date(item.lastLoginTime).toLocaleString(),
            "yyyy/MM/dd HH:mm:ss"
          )
          : "",
        addDate: item.addDate
          ? format(
            new Date(item.addDate).toLocaleString(),
            "yyyy/MM/dd"
          )
          : "",
      }));
      setAdmins(convertData);
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  const handleCreateAdmin = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      await API.post(`admin`, {
        ...data,
        isAdmin: data.isAdmin === "0" ? false : true,
        isContentManageAdmin: data.isAdmin === "0" ? data.isContentManageAdmin : false,
        isUserManageAdmin: data.isAdmin === "0" ? data.isUserManageAdmin : false,
      });
      toast.success("管理者が登録されました。");
      document
        .querySelector<HTMLButtonElement>(
          "#modalCreateAdmin > div > div > div.modal-header > button"
        )?.click();
      fetchAdmins();
    } catch (err) {
      console.error("管理者の登録に失敗しました。もう一度お試しください。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleEditAdmin = async (data: any) => {
    try {
      dispatch(setIsApiLoading(true));
      await API.put(`admin/${activeAdmin?.id}`, {
        ...data,
        isAdmin: data.isAdmin === "0" ? false : true,
        isContentManageAdmin: data.isAdmin === "0" ? data.isContentManageAdmin : false,
        isUserManageAdmin: data.isAdmin === "0" ? data.isUserManageAdmin : false,
      });
      toast.success("管理者が変更されました。");
      document
        .querySelector<HTMLButtonElement>(
          "#modalEditAdmin > div > div > div.modal-header > button"
        )
        ?.click();
      fetchAdmins();
    } catch (err) {
      console.error("管理者の更新に失敗しました。もう一度お試しください。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      if (window.confirm("管理者を削除します。よろしいでしょうか")) {
        dispatch(setIsApiLoading(true));
        await API.delete(`admin/${activeAdmin?.id}`);
        toast.success("管理者が削除されました。");
        document
          .querySelector<HTMLButtonElement>(
            "#modalEditAdmin > div > div > div.modal-header > button"
          )
          ?.click();
        fetchAdmins();
      }
    } catch (err) {
      console.error("管理者の削除に失敗しました。もう一度お試しください。");
    } finally {
      dispatch(setIsApiLoading(false));
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  return (
    <>
      <Helmet>
        <title>管理者管理</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between">
              <div className="form-search row g-2 align-items-center">
                <div className="col-auto">
                  <label htmlFor="adminSearch" className="form-label mb-0">
                    管理者検索
                  </label>
                  <input
                    type="text"
                    id="adminSearch"
                    className="form-select"
                    placeholder="検索..."
                    value={adminSearch}
                    onChange={(e) => setAdminSearch(e.target.value)}
                  />
                </div>
                <div className="col-auto">
                  <label htmlFor="searchType" className="form-label mb-0">
                    ステータス
                  </label>
                  <select
                    id="searchStatus"
                    className="form-select"
                    defaultValue="0"
                    value={searchStatus}
                    onChange={(e) => {
                      setSearchStatus(e.target.value);
                    }}
                  >
                    {ADMIN_STATUS.map((status) => (
                      <option value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-auto">
                  <label
                    htmlFor="status"
                    className="form-label mb-0"
                    style={{ color: "#fff" }}
                  >
                    &nbsp;
                  </label>
                  <button
                    className="btn_submit btn btn-primary"
                    onClick={fetchAdmins}
                  >
                    検索
                  </button>
                </div>
              </div>
              <div className="form-search row g-2 align-items-center">
                <div className="col-auto">
                  <label
                    htmlFor="status"
                    className="form-label mb-0"
                    style={{ color: "#fff" }}
                  >
                    &nbsp;
                  </label>
                  <button
                    className="btn_submit btn btn-danger"
                    data-bs-toggle="modal"
                    data-bs-target="#modalCreateAdmin"
                  >
                    <i className="fas fa-plus" />
                    &nbsp;&nbsp;作成
                  </button>
                </div>
              </div>
            </div>
            <div className="card">
              <div className="card-body">
                <div className="table-responsive">
                  <div
                    id="example3_wrapper"
                    className="dataTables_wrapper no-footer"
                  >
                    <Loading />
                    {!loading && (
                      <DataTable
                        data={admins}
                        className="display"
                        options={{
                          autoWidth: true,
                          rowCallback: (row, data) => {
                            (row as HTMLTableRowElement).onclick = () =>
                              setActiveAdmin(data);
                          },
                          stripeClasses: [],
                          order: [[4, "desc"]],
                          searching: false,
                          destroy: true,
                          info: false,
                          paging: true,
                          language: {
                            lengthMenu: "レコード数&nbsp;_MENU_",
                            emptyTable: "データがありません。",
                          },
                          layout: {
                            topStart: null,
                            topEnd: null,
                            bottomStart: "pageLength",
                            bottomEnd: "paging",
                          },
                          columnDefs: [
                            { targets: "_all", className: "text-start" },
                          ],
                        }}
                        columns={[
                          {
                            title: "View",
                            className: "block_mb",
                            orderable: false,
                            data: null,
                            render: (data, type, row) => {
                              return `<i class="flaticon-381-add-1" data-bs-toggle="modal" data-bs-target="#modalEditAdmin"></i>`;
                            },
                          },
                          {
                            title: "管理者ID",
                            data: "id",
                            orderable: true,
                            className: "text-start",
                          },
                          {
                            title: "管理者名",
                            data: "name",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            title: "メールアドレス",
                            data: "email",
                            className: "text-start",
                          },
                          {
                            title: "ステータス",
                            data: "statusText",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            title: "最終ログイン",
                            data: "lastLoginTime",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            title: "登録日",
                            data: "addDate",
                            className: "mb_none text-start",
                            orderable: true,
                          },
                          {
                            orderable: false,
                            className: "mb_none",
                            data: null,
                            render: (data, type, row) => {
                              return `<div class="d-flex justify-content-center align-items-center">
                                <button class="btn btn-primary shadow btn-xs sharp me-1" data-bs-toggle="modal" data-bs-target="#modalEditAdmin"><i class="fa fa-pencil"></i></button>
                              </div>`;
                            },
                          },
                        ]}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal
        id="modalEditAdmin"
        ref={modalRef}
        title={
          <>
            <h4 className="modal-title fw-bold">管理者編集</h4>
            <p>管理者ID：{activeAdmin?.id}</p>
          </>
        }
        onClose={() => {
          editForm.reset();
          setActiveAdmin(null);
        }}
      >
        {
          activeAdmin &&
          <Form onSubmit={editForm.handleSubmit(handleEditAdmin)}>
            <Form.Group as={Row} className="form-group mb-3">
              <Form.Label column sm="4" md="4">
                管理者名
              </Form.Label>
              <Col sm="8">
                <Form.Control
                  type="text"
                  onChange={(e) => {
                    setActiveAdmin({ ...activeAdmin, name: e.target.value });
                  }}
                  isInvalid={!!editForm.formState.errors.name}
                  {...editForm.register("name")}
                />
                <Form.Control.Feedback type="invalid">
                  {editForm.formState.errors.name?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="form-group mb-3">
              <Form.Label column sm="4" md="4" style={{ fontSize: "0.9rem" }}>
                メールアドレス
              </Form.Label>
              <Col sm="8">
                <Form.Control
                  type="text"
                  onChange={(e) => {
                    setActiveAdmin({ ...activeAdmin, email: e.target.value });
                  }}
                  isInvalid={!!editForm.formState.errors.email}
                  {...editForm.register("email")}
                />
                <Form.Control.Feedback type="invalid">
                  {editForm.formState.errors.email?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="form-group mb-3">
              <Form.Label column sm="4" md="4" style={{ fontSize: "0.9rem" }}>
                パスワード
              </Form.Label>
              <Col sm="8">
                <Form.Control
                  type="password"
                  onChange={(e) => {
                    setActiveAdmin({ ...activeAdmin, password: e.target.value });
                  }}
                  isInvalid={!!editForm.formState.errors.password}
                  {...editForm.register("password")}
                />
                <Form.Control.Feedback type="invalid">
                  {editForm.formState.errors.password?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="form-group mb-3">
              <Form.Label column sm="4" md="4">
                ステータス
              </Form.Label>
              <Col sm="8">
                <Form.Select
                  isInvalid={!!editForm.formState.errors.status}
                  defaultValue={activeAdmin?.status}
                  {...editForm.register("status")}
                >
                  {ADMIN_STATUS.map((item) => (
                    <option value={item.value} key={item.value}>
                      {item.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {editForm.formState.errors.status?.message}
                </Form.Control.Feedback>
              </Col>
            </Form.Group>
            <Form.Group as={Row} className="form-group mb-3">
              <Form.Label column sm="4" md="4">
                権限
              </Form.Label>
              <Col sm="8" className="mt-2">
                <div className="mb-3">
                  <Form.Check
                    inline
                    type="radio"
                    label="無制限（ADMIN）"
                    name="isAdmin"
                    value="1"
                    defaultChecked={activeAdmin?.isAdmin ?? false}
                    {...editForm.register("isAdmin")}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    label="制限付き"
                    name="isAdmin"
                    value="0"
                    defaultChecked={!activeAdmin?.isAdmin}
                    {...editForm.register("isAdmin")}
                  />
                  <div
                    className="mb-3"
                    style={{
                      marginLeft: "1rem",
                      display: showSubRolesEdit ? "block" : "none",
                    }}
                  >
                    <Form.Check
                      inline
                      label="コンテンツ管理"
                      name="isContentManageAdmin"
                      type="checkbox"
                      defaultChecked={activeAdmin?.isContentManageAdmin}
                      {...editForm.register("isContentManageAdmin")}
                    />
                    <Form.Check
                      inline
                      label="ユーザー管理"
                      name="isUserManageAdmin"
                      type="checkbox"
                      defaultChecked={activeAdmin?.isUserManageAdmin}
                      {...editForm.register("isUserManageAdmin")}
                    />
                  </div>
                </div>
                <div className="invalid-feedback d-block">
                  {editForm.formState.errors.isAdmin?.message}
                  {editForm.formState.errors.isContentManageAdmin?.message || editForm.formState.errors.isUserManageAdmin?.message}
                </div>
              </Col>
            </Form.Group>
            <div className="btn_group text-center">
              <button
                type="submit"
                className="btn btnStand btn_save btn-warning me-2"
              >
                保存
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-danger"
                onClick={handleDeleteAdmin}
              >
                アカウントを削除
              </button>
            </div>
          </Form>
        }
      </Modal>
      <Modal
        id="modalCreateAdmin"
        title={
          <>
            <h4 className="modal-title fw-bold">管理者登録</h4>
          </>
        }
        onClose={() => {
          createForm.reset();
        }}
      >
        <Form onSubmit={createForm.handleSubmit(handleCreateAdmin)}>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="4" md="4">
              管理者名
            </Form.Label>
            <Col sm="8">
              <Form.Control
                type="text"
                isInvalid={!!createForm.formState.errors.name}
                {...createForm.register("name")}
              />
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.name?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="4" md="4" style={{ fontSize: "0.9rem" }}>
              メールアドレス
            </Form.Label>
            <Col sm="8">
              <Form.Control
                type="text"
                isInvalid={!!createForm.formState.errors.email}
                {...createForm.register("email")}
              />
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.email?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="4" md="4" style={{ fontSize: "0.9rem" }}>
              パスワード
            </Form.Label>
            <Col sm="8">
              <Form.Control
                type="password"
                isInvalid={!!createForm.formState.errors.password}
                {...createForm.register("password")}
              />
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.password?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="4" md="4">
              ステータス
            </Form.Label>
            <Col sm="8">
              <Form.Select
                isInvalid={!!createForm.formState.errors.status}
                {...createForm.register("status")}
              >
                {ADMIN_STATUS.map((item) => (
                  <option value={item.value} key={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {createForm.formState.errors.status?.message}
              </Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="form-group mb-3">
            <Form.Label column sm="4" md="4">
              権限
            </Form.Label>
            <Col sm="8" className="mt-2">
              <div className="mb-3">
                <Form.Check
                  inline
                  type="radio"
                  label="無制限（ADMIN）"
                  name="isAdmin"
                  value="1"
                  {...createForm.register("isAdmin")}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="制限付き"
                  name="isAdmin"
                  value="0"
                  {...createForm.register("isAdmin")}
                />
                <div
                  className="mb-3"
                  style={{
                    marginLeft: "1rem",
                    display: showSubRolesCreate ? "block" : "none",
                  }}
                >
                  <Form.Check
                    inline
                    label="コンテンツ管理"
                    name="isContentManageAdmin"
                    type="checkbox"
                    {...createForm.register("isContentManageAdmin")}
                  />
                  <Form.Check
                    inline
                    label="ユーザー管理"
                    name="isUserManageAdmin"
                    type="checkbox"
                    {...createForm.register("isUserManageAdmin")}
                  />
                </div>
              </div>
              <div className="invalid-feedback d-block">
                {createForm.formState.errors.isAdmin?.message}
                {createForm.formState.errors.isContentManageAdmin?.message || createForm.formState.errors.isUserManageAdmin?.message}
              </div>
            </Col>
          </Form.Group>
          <div className="btn_group text-center">
            <button
              type="submit"
              className="btn btnStand btn_save btn-primary"
            >
              登録
            </button>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default Admins;
